import { AppEventNames } from "@bezoned/shared/types/app-event-names.js";
import { useQueryClient } from "@tanstack/react-query";
import { parseAsBoolean, parseAsStringEnum, useQueryState } from "nuqs";
import { useCallback, useEffect, useRef } from "react";

import { FullScreenLoader } from "../../components/ui/loaders/full-screen-loader.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAdminConsent } from "../auth/use-admin-consent.js";
import {
  consumeInteractiveAdminConsent,
  markInteractiveAdminConsentPending,
  trackAuthenticatedOnboardingEvent,
} from "./onboarding-tracker.js";
import { RetryAdminPermissions } from "./retry-admin-permissions.js";
import { TenantCreationInProgress } from "./tenant-creation-in-progress.js";

export const CreateTenant = ({ tenantId }: { tenantId: string }) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useQueryState(
    "step",
    parseAsStringEnum(["permissions", "create"]).withDefault("permissions")
  );
  const [hasError, setHasError] = useQueryState("error", parseAsBoolean);
  const [consentOutcome] = useQueryState(
    "consentOutcome",
    parseAsStringEnum(["denied", "error"])
  );
  const handleTenantCreated = useCallback(() => {
    // Keep the onboarding guard on the success path after tenant creation.
    // An immediate refetch here can briefly return `exists: false` before the
    // backend state becomes visible everywhere, which puts the user back into
    // the onboarding flow after a successful create.
    queryClient.setQueryData(["tenantExists"], {
      exists: true as const,
      permissions: { needsUpgrade: false },
      type: "regular" as const,
    });
  }, [queryClient]);
  const hasCompletedAdminConsent = useRef(step === "create");
  const lastReportedAdminConsentOutcome = useRef<string | null>(null);
  const hasRequestedConsent = useRef(false);
  const openAdminConsent = useAdminConsent({
    flow: "onboarding",
    popupSize: {
      height: 535,
      width: 600,
    },
    tenantId,
  });

  const recordAdminConsentOutcome = useCallback(
    (
      eventName:
        | "consent.admin.succeeded"
        | "consent.admin.already-granted"
        | "consent.admin.denied"
        | "consent.admin.error"
    ) => {
      if (lastReportedAdminConsentOutcome.current === eventName) {
        return;
      }

      lastReportedAdminConsentOutcome.current = eventName;
      trackAuthenticatedOnboardingEvent(eventName);
    },
    []
  );

  const askForConsent = useCallback(() => {
    hasRequestedConsent.current = true;
    setHasError(null);
    setStep("permissions");

    const requestConsent = async () => {
      try {
        void markInteractiveAdminConsentPending();
        const result = await openAdminConsent();
        if (result === "denied") {
          consumeInteractiveAdminConsent();
          recordAdminConsentOutcome(AppEventNames.ConsentAdminDenied);
          setHasError(true);
          return;
        }

        if (result !== "completed") {
          return;
        }

        hasCompletedAdminConsent.current = true;
        recordAdminConsentOutcome(
          consumeInteractiveAdminConsent()
            ? AppEventNames.ConsentAdminSucceeded
            : AppEventNames.ConsentAdminAlreadyGranted
        );
        setStep("create");
      } catch (error: unknown) {
        consumeInteractiveAdminConsent();
        recordErrorInSentry(error, {
          extra: {
            source: "onboarding-admin-consent",
          },
        });
        setStep("permissions");
        setHasError(true);
      }
    };

    void requestConsent();
  }, [openAdminConsent, recordAdminConsentOutcome, setHasError, setStep]);

  useEffect(() => {
    if (step === "create") {
      hasCompletedAdminConsent.current = true;
      hasRequestedConsent.current = false;
      recordAdminConsentOutcome(
        consumeInteractiveAdminConsent()
          ? AppEventNames.ConsentAdminSucceeded
          : AppEventNames.ConsentAdminAlreadyGranted
      );
      return;
    }

    if (step !== "permissions") {
      hasRequestedConsent.current = false;
      return;
    }

    if (hasCompletedAdminConsent.current) {
      setStep("create");
      return;
    }

    if (hasError) {
      if (lastReportedAdminConsentOutcome.current) {
        return;
      }

      consumeInteractiveAdminConsent();
      recordAdminConsentOutcome(
        consentOutcome === "denied"
          ? AppEventNames.ConsentAdminDenied
          : AppEventNames.ConsentAdminError
      );
      return;
    }

    if (hasRequestedConsent.current) {
      return;
    }

    hasRequestedConsent.current = true;
    askForConsent();
  }, [
    askForConsent,
    consentOutcome,
    hasError,
    recordAdminConsentOutcome,
    setStep,
    step,
  ]);

  if (hasError) {
    return <RetryAdminPermissions onRetry={askForConsent} />;
  }

  if (step === "permissions") {
    return <FullScreenLoader message="Requesting Admin Consent..." />;
  }

  // Step == "create"
  return <TenantCreationInProgress onSuccess={handleTenantCreated} />;
};
